import React from 'react';
import { Alert, AlertDescription, AlertIcon } from '@chakra-ui/alert';
import { Box, Center, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { NotificationListItem } from './NotificationListItem';
import { useNotificationSearchMatcher } from './notifications-helper';
import { NotificationGrouping, useGetNotifications } from './notificationsApi';
import { NotificationsListSkeletonLoader } from './NotificationsListSkeletonLoader';

type Props = {
  groupBy: NotificationGrouping;
  // The query limit — grows by a page each time "Load more" is clicked
  limit?: number;
  // Only notifications whose title matches this search term are fetched
  searchQuery?: string;
  isArchived?: boolean;
  isEditMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (notificationId: string) => void;
  onSwipeSelect?: (notificationId: string) => void;
};

export function NotificationsListChronological({
  groupBy,
  limit = 10,
  searchQuery = '',
  isArchived = false,
  isEditMode = false,
  selectedIds,
  onToggleSelect,
  onSwipeSelect,
}: Props) {
  const {
    data: { chronological },
    isLoading,
    isFetching,
    isError,
  } = useGetNotifications(groupBy, { limit, offset: 0, archived: isArchived, search: searchQuery || undefined });
  const { t } = useTranslation();
  const matchesSearch = useNotificationSearchMatcher(searchQuery);
  const fetchedNotifications = chronological?.data?.results;
  // Filter by title client-side as well, so search works even if the API ignores the `search` param
  const notifications = fetchedNotifications?.filter(matchesSearch);
  const totalNumNotifications = chronological?.data?.count ?? 0;
  // True while a larger page is in flight after growing the limit (but not during background polling)
  const isFetchingMore =
    isFetching &&
    !!fetchedNotifications &&
    fetchedNotifications.length < limit &&
    fetchedNotifications.length < totalNumNotifications;
  const notificationsList = isLoading ? (
    <NotificationsListSkeletonLoader />
  ) : (
    notifications?.map((notification) => (
      <NotificationListItem
        notification={notification}
        key={notification.notification_id}
        isArchived={isArchived}
        isEditMode={isEditMode}
        isSelected={selectedIds?.has(notification.notification_id)}
        onToggleSelect={onToggleSelect}
        onSwipeSelect={onSwipeSelect}
      />
    ))
  );

  if (isError) {
    return (
      <Alert data-testid="notifications-error-alert" status="error" my={4}>
        <AlertIcon w={8} h={8} />
        <AlertDescription py={4}>
          {t('Notifications.error fetching notifications')}.{' '}
          {`${t('Common.please try again')} ${t('Common.if this persists contact support')}`}.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Box>
      <Text data-testid="list-type-title">{t('Notifications.chronological').toUpperCase()}</Text>
      <Box data-testid="notifications-list-chronological" mb={3}>
        {!notifications?.length && !isLoading ? (
          <Center data-testid="no-notifications-message" my={4}>
            <Text>{t('Notifications.no notifications')}</Text>
          </Center>
        ) : (
          notificationsList
        )}

        {isFetchingMore && <NotificationsListSkeletonLoader numItems={2} />}
      </Box>
    </Box>
  );
}
